import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { authApi } from '../services/api'
import ThemeToggle from './ThemeToggle'
import {
  NewProjectIcon,
  CategoryIcon,
  SettingsIcon,
  DashboardIcon,
  ProjectIcon,
  CalendarIcon,
  IdeaIcon,
  LogoutIcon,
  XIcon,
  ToolIcon,
} from './Icons'
import '../styles/AdminSidebar.css'

function AdminSidebar({ currentPage }) {
  const [collapsed, setCollapsed] = useState(() => {
    // Initialize from localStorage
    const saved = localStorage.getItem('adminSidebarCollapsed')
    return saved === 'true'
  })
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  // Persist collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('adminSidebarCollapsed', collapsed.toString())
  }, [collapsed])

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location])

  const handleLogout = async () => {
    try {
      await authApi.logout()
      navigate('/')
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  const menuItems = [
    {
      type: 'section',
      label: 'Admin',
      items: [
        { icon: NewProjectIcon, label: 'New Project', path: '/admin', exactMatch: true },
        { icon: CategoryIcon, label: 'Categories', path: '/admin?section=categories' },
        { icon: SettingsIcon, label: 'Site Settings', path: '/admin?section=settings' },
      ]
    },
    {
      type: 'section',
      label: 'Planning',
      items: [
        { icon: DashboardIcon, label: 'Dashboard', path: '/elise/dashboard' },
        { icon: ProjectIcon, label: 'Project Plans', path: '/elise?view=plans' },
        { icon: CalendarIcon, label: 'Calendar', path: '/elise?view=calendar' },
        { icon: IdeaIcon, label: 'Ideas', path: '/elise/ideas' },
        { icon: ToolIcon, label: 'Inventory', path: '/elise/inventory' },
      ]
    }
  ]

  const isActive = (path, exactMatch = false) => {
    const currentPath = window.location.pathname
    const currentSearch = window.location.search
    const fullPath = currentPath + currentSearch

    if (exactMatch) {
      return currentPath === path && !currentSearch
    }

    return fullPath === path || fullPath.startsWith(path)
  }

  const renderNavContent = (isMobile = false) => (
    <>
      {menuItems.map((section, sectionIdx) => (
        <div key={sectionIdx} className="sidebar-section">
          {(isMobile || !collapsed) && section.label && (
            <div className="sidebar-section-label">{section.label}</div>
          )}
          {section.items.map((item, itemIdx) => {
            const IconComponent = item.icon
            return (
              <Link
                key={itemIdx}
                to={item.path}
                className={`sidebar-btn ${isActive(item.path, item.exactMatch) ? 'active' : ''}`}
                title={item.label}
              >
                <span className="sidebar-icon">
                  <IconComponent size={20} />
                </span>
                {(isMobile || !collapsed) && <span className="sidebar-label">{item.label}</span>}
              </Link>
            )
          })}
        </div>
      ))}

      <div className="sidebar-theme-toggle">
        <ThemeToggle compact={!isMobile && collapsed} />
      </div>

      <button className="sidebar-btn logout-btn" onClick={handleLogout} title="Logout">
        <span className="sidebar-icon">
          <LogoutIcon size={20} />
        </span>
        {(isMobile || !collapsed) && <span className="sidebar-label">Logout</span>}
      </button>
    </>
  )

  return (
    <>
      {/* Mobile Header Bar */}
      <div className="admin-mobile-header">
        <h2>Admin Panel</h2>
        <button
          className={`admin-hamburger ${mobileMenuOpen ? 'open' : ''}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      {/* Mobile Slide-out Menu */}
      <div className={`admin-mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="admin-mobile-overlay" onClick={() => setMobileMenuOpen(false)} />
        <nav className="admin-mobile-nav">
          <div className="admin-mobile-nav-header">
            <h2>Admin Panel</h2>
            <button
              className="admin-mobile-close"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              <XIcon size={24} />
            </button>
          </div>
          <div className="admin-mobile-nav-content">
            {renderNavContent(true)}
          </div>
        </nav>
      </div>

      {/* Desktop Sidebar */}
      <aside className={`admin-sidebar ${collapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <h2>{!collapsed && 'Admin Panel'}</h2>
          <button
            className="sidebar-toggle"
            onClick={() => setCollapsed(!collapsed)}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? '»' : '«'}
          </button>
        </div>

        <nav className="sidebar-nav">
          {renderNavContent(false)}
        </nav>
      </aside>
    </>
  )
}

export default AdminSidebar
